"use client";
import React, { useState, useCallback, useEffect, useRef } from "react";
import { X, AlertTriangle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { getStrapiMedia } from "@aicodepro/web-builder-utils";
import { renderButtonStyle } from "../utils/render-button-style";

interface FormData {
  company: string;
  websiteUrl: string;
  primaryKeywords: string;
  email: string;
  city: string;
  name: string;
  phone: string;
  placeId: string;
}

interface Suggestion {
  id: string;
  description: string;
}

interface Picture {
  data: Array<{
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  }>;
}

interface FormModalProps {
  data: {
    title: string;
    description: string;
    image: Picture;
    useH1Tag?: boolean;
  };
}

const debounce = <F extends (...args: any[]) => any>(
  func: F,
  delay: number
): ((...args: Parameters<F>) => void) => {
  let timerId: NodeJS.Timeout | null = null;

  return (...args: Parameters<F>) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const CustomModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
      document.body.classList.add("overflow-hidden"); // Prevent body scroll
    } else {
      document.body.classList.remove("overflow-hidden"); // Restore scroll
    }

    // Cleanup when component unmounts or dialog closes
    return () => document.body.classList.remove("overflow-hidden");
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
      <div
        className="rounded-md flex flex-col w-full max-w-[420px] relative overflow-hidden shadow-lg shadow-slate-600"
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white w-6 h-6 rounded-full bg-th-bg-col hover:bg-[#50408a] transition-colors flex items-center justify-center"
          aria-label="Close modal"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
};

export default function FormModal({ data }: FormModalProps) {
  const websiteAuditRef = useRef<HTMLDivElement | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [focusedInput, setFocusedInput] = useState<"company" | "city" | null>(
    null
  );
  const [targetLocation, setTargetLocation] = useState<any | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCity, setShowCity] = useState(true);
  const [isSearching, setIsSearching] = useState({
    company: false,
    city: false,
  });
  const [formData, setFormData] = useState<FormData>({
    company: "",
    websiteUrl: "",
    primaryKeywords: "",
    email: "",
    city: "",
    name: "",
    phone: "",
    placeId: "",
  });
  const [suggestions, setSuggestions] = useState<{
    company: Suggestion[];
    city: Suggestion[];
  }>({
    company: [],
    city: [],
  });

  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams.get("scroll") === "true") {
      setTimeout(() => {
        if (websiteAuditRef.current) {
          websiteAuditRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 100);
    }
  }, [pathname, searchParams]);

  const fetchSuggestions = async (field: "company" | "city", value: string) => {
    setIsSearching((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await fetch(
        `https://www.mywebaudit.com/api/v2/lead-widget/company-search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ need_types: field, search_term: value }),
        }
      );
      const data = await response.json();
      setSuggestions((prev) => ({ ...prev, [field]: data.suggestions }));
    } finally {
      setIsSearching((prev) => ({ ...prev, [field]: false }));
    }
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 300),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));

    if (name === "company" || name === "city") {
      debouncedFetchSuggestions(name, value);
    }
  };

  const handleSuggestionSelect = async (
    field: "company" | "city",
    value: { name: string; placeId: string }
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value.name }));

    const placeResponse = await fetch(
      "https://www.mywebaudit.com/api/v2/lead-widget/company-place-detail",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ place_id: value.placeId }),
      }
    );
    const placeData = await placeResponse.json();
    if (field === "company") {
      setFormData((prev) => ({
        ...prev,
        websiteUrl: placeData.place.website,
        city: placeData.place.formatted_address,
        placeId: value.placeId,
      }));
      setShowCity(false);
    } else if (field === "city") {
      setFormData((prev) => ({
        ...prev,
        placeId: value.placeId,
      }));
    }
    setTargetLocation(placeData.place);
    setSuggestions((prev) => ({ ...prev, [field]: [] }));
  };
  const handleFocus = (field: "company" | "city") => {
    setFocusedInput(field);
  };

  const handleBlur = () => {
    // Use setTimeout to allow clicking on suggestions before hiding them
    setTimeout(() => setFocusedInput(null), 200);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    setSuccess(false);
    setFormData({
      company: "",
      websiteUrl: "",
      primaryKeywords: "",
      email: "",
      city: "",
      placeId: "",
      name: "",
      phone: "",
    });
    setError(null);
    setTargetLocation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.city === "") {
      return setError("Please select a city");
    }
    const formSubmitData = new FormData();
    formSubmitData.append("company_name", formData.company);
    formSubmitData.append("website_url", formData.websiteUrl);
    formSubmitData.append("primary_keyword", formData.primaryKeywords);
    formSubmitData.append("email", formData.email);
    formSubmitData.append("name", formData.name);
    formSubmitData.append("phone", formData.phone);
    formSubmitData.append("target_location", formData.city);
    formSubmitData.append("mwa_custom_field_1", "null");
    formSubmitData.append("mwa_custom_field_2", "null");
    formSubmitData.append(
      "company_places",
      targetLocation?.website ? targetLocation : "manual"
    );
    formSubmitData.append(
      "company_website_url",
      targetLocation?.website ? targetLocation?.website : "null"
    );
    formSubmitData.append(
      "mwa_locality",
      targetLocation?.address_components?.[1]?.long_name || "null"
    );
    formSubmitData.append("mwa_postal_town", "");
    formSubmitData.append(
      "mwa_administrative_area_level_1",
      targetLocation?.address_components?.[3]?.long_name || "null"
    );
    formSubmitData.append("mwa_administrative_area_level_2", "");
    formSubmitData.append("mwa_postal_code", "");
    formSubmitData.append(
      "mwa_formatted_address",
      targetLocation?.formatted_address || "null"
    );
    formSubmitData.append(
      "mwa_country",
      targetLocation?.address_components?.[4]?.long_name || "null"
    );
    formSubmitData.append("mwa_location_display", formData.city);
    formSubmitData.append("mwa_place_id", formData.placeId);
    formSubmitData.append("mwa_report_type", "website");
    formSubmitData.append("mwa_utm_source", "");
    formSubmitData.append("mwa_utm_medium", "");
    formSubmitData.append("mwa_utm_campaign", "");
    formSubmitData.append("mwa_utm_term", "");
    formSubmitData.append("mwa_utm_content", "");
    formSubmitData.append("widget_token", "78b380");

    const formSubmit = await fetch(
      "https://www.mywebaudit.com/api/v2/lead-widget/submit/audit",
      {
        method: "POST",
        body: formSubmitData,
      }
    );
    const formSubmitDataJson = await formSubmit.json();
    if (formSubmitDataJson?.status === "success") {
      setSuccess(true);
      setFormData({
        company: "",
        websiteUrl: "",
        primaryKeywords: "",
        email: "",
        city: "",
        placeId: "",
        name: "",
        phone: "",
      });
      setTargetLocation(null);
    } else {
      setError("Something went wrong. Please try again.");
    }
  };

  useEffect(() => {
    const existingScript = document.getElementById("mwa-script");
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = "mwa-script";
      script.async = true;
      script.src =
        "https://www.mywebaudit.com/w/js/lead-widget.js?t=" +
        new Date().getTime();
      document.body.appendChild(script);
    }
  }, []);

  const imgUrl = getStrapiMedia(data?.image?.data[0]?.attributes?.url);

  return (
    <>
      <section
        ref={websiteAuditRef}
        className="mx-auto px-8 lg:py-20 py-12 bg-th-bg-col-rich overflow-hidden relative"
        // style={{
        //   backgroundImage: imgUrl ? `url(${imgUrl})` : "",
        //   backgroundSize: "cover",
        //   backgroundRepeat: "no-repeat",
        //   backgroundPosition: "center",
        // }}
      >
        <div className="w-full flex lg:flex-row md:flex-row flex-col justify-center items-center lg:gap-0 md:gap-4 gap-16 px-3 py-5">
          <div className="relative lg:w-[50%] md:w-[50%] w-full text-left flex flex-col justify-center items-start gap-8">
            <div className="relative ">
              {
                data?.useH1Tag ? (
                  <h1 className="text-3xl lg:text-4xl md:text-4xl">
                {data?.title?.split(" ").map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line == "Audit" ? (
                      <span className="relative whitespace-nowrap text-orange-400">
                        <Image
                          src="https://oextech.blr1.digitaloceanspaces.com/AiCodePro/line1.svg"
                          alt="Highlight Background"
                          width={100}
                          height={100}
                          className="absolute top-[-64%] left-[2px] h-[150px] w-full"
                        />
                        <span className="relative">{line}</span>
                      </span>
                    ) : (
                      <span className="leading-snug">{line}</span>
                    )}
                    {index !== array.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h1>
                ) : (
                  <h2 className="text-3xl font-bold lg:text-4xl md:text-4xl">
                {data?.title?.split(" ").map((line, index, array) => (
                  <React.Fragment key={index}>
                    {line == "Audit" ? (
                      <span className="relative whitespace-nowrap text-orange-400">
                        <Image
                          src="https://oextech.blr1.digitaloceanspaces.com/AiCodePro/line1.svg"
                          alt="Highlight Background"
                          width={100}
                          height={100}
                          className="absolute top-[-64%] left-[2px] h-[150px] w-full"
                        />
                        <span className="relative">{line}</span>
                      </span>
                    ) : (
                      <span className="leading-snug">{line}</span>
                    )}
                    {index !== array.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h2>
                )
              }
            </div>
            <p className="text-md md:text-lg lg:text-lg text-black text-left max-w-[500px]">
              {data?.description}
            </p>
            <button
              onClick={() => setModalIsOpen(true)}
              className="px-8 py-3 text-lg font-semibold rounded-full bg-th-btncol-1 text-th-btntext-2 hover:bg-th-btncol-2 shadow-sm transition-colors duration-200 hover:shadow-none"
            >
              Audit Now
            </button>
          </div>
          <div className="lg:w-[50%] md:w-[50%] w-full flex justify-center items-center">
            <div className="w-72 flex justify-center items-center">
              <Image
                src={
                  "https://oextech.blr1.digitaloceanspaces.com/AiCodePro/important/site-audit.svg"
                }
                alt="site-audit"
                width={100}
                height={100}
                className="object-contain w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
        <div
          role="main"
          id="mwa-key-78b380"
          data-key="78b380"
          className="mwa-widget-container hidden"
        ></div>
      </section>
      <CustomModal isOpen={modalIsOpen} onClose={handleCloseModal}>
        {success ? (
          <div className="flex flex-col items-center justify-center rounded-md py-12 px-6 overflow-hidden bg-zinc-100">
            <div className="flex flex-col items-center text-center mx-auto">
              <div className="mb-4">
                <Image
                  src="https://oextech.blr1.digitaloceanspaces.com/AiCodePro/orange%20checkmark.svg"
                  alt="Checkmark"
                  width={100}
                  height={100}
                  className="cover"
                />
              </div>
              <h2 className="text-4xl font-bold">Thank You</h2>
              <p className="mt-4 text-sm px-2 text-zinc-700">
                We will soon analyze your site's design, security, performance,
                SEO, and visitor conversion. For queries, email us at{" "}
                <Link
                  href={"mailto:support@aicodepro.com"}
                  className="text-th-accent font-semibold"
                >
                  support@aicodepro.com
                </Link>{" "}
                or call{" "}
                <Link
                  href={"tel:+91-8130376767"}
                  className="text-th-accent font-semibold"
                >
                  +91-8130376767
                </Link>{" "}
                to let us know what day and time works best for you.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6 py-10 bg-white">
            <h2 className="text-2xl font-bold text-center mb-3">
              Get Your Free Website Audit
            </h2>
            {/* <h2 className="text-2xl font-bold text-center mb-4"></h2> */}
            <p className="text-center text-gray-600 text-sm mb-6">
              Enter your information below to get your Website Audit
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  onFocus={() => handleFocus("company")}
                  onBlur={handleBlur}
                  placeholder="Company Name *"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  required
                />
                {focusedInput === "company" &&
                  (suggestions.company.length > 0 || isSearching.company) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-sm">
                      {isSearching.company ? (
                        <div className="px-3 py-2 text-xs text-gray-600">
                          Searching...
                        </div>
                      ) : (
                        suggestions.company.map((s: any, index: number) => (
                          <div
                            key={index}
                            className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs border-b"
                            onClick={() =>
                              handleSuggestionSelect("company", {
                                name: s.structured_formatting.main_text,
                                placeId: s.place_id,
                              })
                            }
                          >
                            {s.description}
                          </div>
                        ))
                      )}
                    </div>
                  )}
              </div>

              <div className="relative">
                <input
                  type="url"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleChange}
                  placeholder="Website URL *"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  required
                />
              </div>

              <div className="relative">
                <input
                  type="text"
                  name="primaryKeywords"
                  value={formData.primaryKeywords}
                  onChange={handleChange}
                  placeholder="Primary Keyword"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  required
                />
              </div>

              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email *"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  required
                />
              </div>

              {/* <div className="relative">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number *"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  required
                />
              </div> */}

              {showCity && (
                <div className="relative">
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    onFocus={() => handleFocus("city")}
                    onBlur={handleBlur}
                    placeholder="Target Location *"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-th-card-rich focus:border-transparent outline-none"
                  />

                  {focusedInput === "city" &&
                    (suggestions.city.length > 0 || isSearching.city) && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-sm">
                        {isSearching.city ? (
                          <div className="px-3 py-2 text-xs text-gray-600">
                            Searching...
                          </div>
                        ) : (
                          suggestions.city.map((s: any, index: number) => (
                            <div
                              key={index}
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs"
                              onClick={() =>
                                handleSuggestionSelect("city", {
                                  name: s.structured_formatting.secondary_text,
                                  placeId: s.place_id,
                                })
                              }
                            >
                              {s.description}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                </div>
              )}
              {error && (
                <div className="mt-2 flex items-center justify-start relative text-rose-600 p-3 bg-rose-100 rounded-md">
                  <AlertTriangle className="w-[0.85rem] h-[0.85rem] mr-2" />
                  <p className="text-rose-600 text-xs">{error}</p>
                </div>
              )}
              <button
                type="submit"
                className={`${renderButtonStyle(
                  "primary"
                )} w-full text-white font-semibold py-3 px-4 rounded-md transition-colors text-sm`}
              >
                Get My Free Audit
              </button>
            </form>
          </div>
        )}
      </CustomModal>
    </>
  );
}
